import React, { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, XCircle, FileDown, Wallet, Mail, Send, Link as LinkIcon, PackageCheck, Ban, AlertOctagon, GitMerge, Paperclip, ListChecks, Archive, X, ChevronDown, ChevronRight, ShieldAlert, FileSearch } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../../lib/ist";
import ModalPortal from "../../components/ModalPortal";

const STATUS_STYLE = {
  paid: "text-emerald-300",
  delivered: "text-emerald-200",
  completed: "text-emerald-300",
  approved: "text-blue-300",
  processing: "text-cyan-300",
  pending: "text-amber-300",
  pending_approval: "text-amber-300",
  moderation_hold: "text-orange-300",
  awaiting_documents: "text-amber-400",
  documents_uploaded: "text-cyan-300",
  rejected: "text-rose-300",
  cancelled: "text-rose-300",
  fraud: "text-rose-400",
  merged: "text-slate-400",
  failed: "text-rose-300",
};

const backend = process.env.REACT_APP_BACKEND_URL;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [paying, setPaying] = useState(null);
  const [delivering, setDelivering] = useState(null);
  const [merging, setMerging] = useState(false);          // bulk-select mode
  const [selected, setSelected] = useState(new Set());
  const [mergeDialog, setMergeDialog] = useState(null);
  const [attaching, setAttaching] = useState(null);       // order being attached to invoice
  const [bulkOpen, setBulkOpen] = useState(false);
  const [expandedCascade, setExpandedCascade] = useState(null); // order id currently showing audit drawer
  const [kycDrawer, setKycDrawer] = useState(null);              // order id currently showing KYC files drawer

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "pending") return orders.filter(o => ["pending", "pending_approval"].includes(o.status));
    if (filter === "delivered") return orders.filter(o => o.status === "delivered" || o.status === "completed");
    return orders.filter(o => o.status === filter);
  }, [orders, filter]);

  const approve = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "approved" }); toast.success("Approved & customer notified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (o) => {
    const reason = window.prompt("Rejection reason (sent to customer):") || "";
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "rejected", reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancel = async (o) => {
    const reason = window.prompt("Cancellation reason (sent to customer):") || "";
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "cancelled", reason }); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const flagFraud = async (o) => {
    const reason = window.prompt("Reason for flagging this order as fraud (internal note + customer email):");
    if (!reason) return;
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "fraud", reason }); toast.success("Flagged"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const complete = async (o) => {
    if (!window.confirm("Mark this order COMPLETED? A Service Fulfilment Contract PDF will be auto-generated and emailed to the customer.")) return;
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "completed" }); toast.success("Completed · contract emailed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setProcessing = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/status`, { status: "processing" }); toast.success("Marked processing"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const refundPayGlocal = async (o) => {
    const txt = window.prompt(
      `Refund order ${o.id.slice(0,8).toUpperCase()} via PayGlocal.\n\n` +
      `Captured amount: ${o.payglocal_amount || "—"} ${o.payglocal_currency || ""}\n\n` +
      `Leave the amount blank for a FULL refund, or enter a number for a partial refund:`,
      ""
    );
    if (txt === null) return;
    const amount = txt.trim() ? Number(txt) : null;
    if (amount !== null && (Number.isNaN(amount) || amount <= 0)) {
      toast.error("Enter a valid amount or leave blank for full refund");
      return;
    }
    const reason = window.prompt("Reason for refund (optional, sent for audit):", "") || null;
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/refund-payglocal`, { amount, reason });
      toast.success(`Refund issued · PayGlocal status: ${data?.refund?.status || "OK"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };
  const requestDocs = async (o) => {
    const reason = window.prompt(
      "What documents / verification do you need? (sent to customer email)",
      "Please upload your ID Proof and Address Proof so we can activate this order."
    );
    if (reason === null) return;
    try {
      await api.post(`/admin/orders/${o.id}/status`, { status: "awaiting_documents", reason });
      toast.success("Customer notified — awaiting their upload");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const clearSelection = () => { setSelected(new Set()); setMerging(false); };

  const downloadPdf = (url, filename) => {
    const token = localStorage.getItem("optivize_token");
    fetch(`${backend}${url}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject(new Error("download failed")))
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Order Management</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setBulkOpen(true)}
                  className="btn-ghost !py-2 !px-3 text-xs flex items-center gap-1"
                  data-testid="bulk-contracts-btn">
            <Archive size={13}/> Bulk contracts
          </button>
          <button onClick={() => downloadPdf("/api/admin/orders/cascade-audit.csv", `cascade-audit-${new Date().toISOString().slice(0,10)}.csv`)}
                  className="btn-ghost !py-2 !px-3 text-xs flex items-center gap-1"
                  title="Export every cancelled/fraud refund cascade event as CSV"
                  data-testid="cascade-csv-btn">
            <ShieldAlert size={13}/> Audit CSV
          </button>
          <button onClick={() => setMerging(m => !m)}
                  className={`btn-ghost !py-2 !px-3 text-xs flex items-center gap-1 ${merging ? "!bg-blue-600 !text-white" : ""}`}
                  data-testid="orders-merge-toggle">
            <ListChecks size={13}/> {merging ? "Cancel select" : "Select multiple"}
          </button>
          {merging && selected.size >= 2 && (
            <button onClick={() => setMergeDialog([...selected])}
                    className="btn-primary !py-2 !px-3 text-xs flex items-center gap-1.5"
                    data-testid="orders-merge-confirm-btn">
              <GitMerge size={13}/> Merge {selected.size}
            </button>
          )}
        </div>
      </div>

      <div className="inline-flex rounded-xl bg-slate-900/60 border border-white/5 p-1 overflow-x-auto" data-testid="orders-filter">
        {[
          ["all","All"],["pending","Pending"],["moderation_hold","On hold"],["approved","Approved"],["processing","Processing"],
          ["paid","Paid"],["delivered","Delivered"],["completed","Completed"],
          ["cancelled","Cancelled"],["rejected","Rejected"],["fraud","Fraud"],["merged","Merged"],
        ].map(([k,l]) => (
          <button key={k} onClick={()=>setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-xs transition whitespace-nowrap ${filter===k ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
            data-testid={`filter-${k}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400"/></div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr>
                {merging && <th className="px-3"></th>}
                <th className="py-3 px-4 text-left">Order</th>
                <th className="text-left">Customer</th>
                <th className="text-left">Items</th>
                <th className="text-left">Date</th>
                <th className="text-right">Total</th>
                <th className="text-center">Status</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <React.Fragment key={o.id}>
                <tr className="border-b border-slate-800" data-testid={`order-row-${o.id}`}>
                  {merging && (
                    <td className="px-3">
                      <input type="checkbox" checked={selected.has(o.id)}
                             onChange={()=>toggleSelect(o.id)}
                             data-testid={`order-select-${o.id}`}/>
                    </td>
                  )}
                  <td className="py-3 px-4 text-cyan-300 font-mono">
                    #{o.id.slice(0,8)}
                    {o.linked_invoice_number && <div className="text-[10px] text-slate-500">↳ {o.linked_invoice_number}</div>}
                    {o.merged_into && <div className="text-[10px] text-slate-500">→ {o.merged_into.slice(0,8)}</div>}
                  </td>
                  <td className="text-slate-300 max-w-[160px] truncate">{o.customer?.name || o.customer?.email || "—"}</td>
                  <td className="text-slate-300 max-w-[240px] truncate">{(o.items||[]).map(i=>i.name).join(", ")}</td>
                  <td className="text-slate-400">{fmtDate(o.created_at)}</td>
                  <td className="text-right text-white">₹{(o.total_inr || o.total || 0).toLocaleString()}</td>
                  <td className="text-center">
                    <div className="inline-flex items-center gap-1">
                      <span className={STATUS_STYLE[o.status] || "text-slate-400"}>{o.status}</span>
                      {o.refund_cascade && ["cancelled","fraud"].includes(o.status) && (
                        <button
                          onClick={() => setExpandedCascade(expandedCascade === o.id ? null : o.id)}
                          className="text-rose-300 hover:text-rose-200 inline-flex items-center"
                          title="Show refund cascade audit"
                          data-testid={`cascade-toggle-${o.id}`}>
                          {expandedCascade === o.id ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                          <ShieldAlert size={12} className="ml-0.5"/>
                        </button>
                      )}
                    </div>
                    {o.refund_cascade && ["cancelled","fraud"].includes(o.status) && (
                      <div className="text-[10px] text-rose-400/80 mt-0.5" data-testid={`cascade-chip-${o.id}`}>
                        −{o.refund_cascade.credits_revoked_actual || 0} cr · {o.refund_cascade.invoices_updated || 0} inv
                      </div>
                    )}
                    {o.status === "moderation_hold" && (
                      <div className="text-[10px] text-orange-400/80 mt-0.5 max-w-[160px] truncate" title={o.moderation_hold_reason || ""} data-testid={`hold-reason-${o.id}`}>
                        ⚠ {o.moderation_hold_reason || "flagged customer"}
                      </div>
                    )}
                  </td>
                  <td className="text-right pr-4">
                    <div className="inline-flex items-center gap-1">
                      {["pending","pending_approval","moderation_hold","documents_uploaded"].includes(o.status) && (
                        <>
                          <button onClick={()=>approve(o)} title="Approve" className="text-emerald-300 hover:text-emerald-200 p-1.5" data-testid={`approve-${o.id}`}><CheckCircle2 size={16}/></button>
                          <button onClick={()=>reject(o)} title="Reject" className="text-rose-300 hover:text-rose-200 p-1.5" data-testid={`reject-${o.id}`}><XCircle size={16}/></button>
                          <button onClick={()=>requestDocs(o)} title="Require additional documents" className="text-amber-300 hover:text-amber-200 p-1.5" data-testid={`require-docs-${o.id}`}><FileSearch size={16}/></button>
                        </>
                      )}
                      {["awaiting_documents","documents_uploaded"].includes(o.status) && (
                        <button onClick={()=>setKycDrawer(kycDrawer === o.id ? null : o.id)} title="View uploaded KYC files" className="text-cyan-300 hover:text-cyan-200 p-1.5" data-testid={`kyc-view-${o.id}`}><Paperclip size={16}/></button>
                      )}
                      {!["paid","rejected","cancelled","completed","fraud","merged"].includes(o.status) && (
                        <button onClick={()=>setPaying(o)} title="Mark paid manually" className="text-blue-300 hover:text-blue-200 p-1.5" data-testid={`mark-paid-${o.id}`}><Wallet size={16}/></button>
                      )}
                      {["approved","paid"].includes(o.status) && (
                        <button onClick={()=>setProcessing(o)} title="Mark processing" className="text-cyan-300 hover:text-cyan-200 p-1.5" data-testid={`processing-${o.id}`}><Send size={16}/></button>
                      )}
                      {["paid","processing","approved","delivered"].includes(o.status) && !["completed","cancelled","fraud","merged"].includes(o.status) && (
                        <button onClick={()=>complete(o)} title="Mark completed (auto-emails service contract)" className="text-emerald-300 hover:text-emerald-200 p-1.5" data-testid={`complete-${o.id}`}><PackageCheck size={16}/></button>
                      )}
                      {!["cancelled","rejected","completed","fraud","merged"].includes(o.status) && (
                        <button onClick={()=>cancel(o)} title="Cancel" className="text-rose-300 hover:text-rose-200 p-1.5" data-testid={`cancel-${o.id}`}><Ban size={16}/></button>
                      )}
                      {!["fraud","merged","completed"].includes(o.status) && (
                        <button onClick={()=>flagFraud(o)} title="Flag as fraud" className="text-orange-300 hover:text-orange-200 p-1.5" data-testid={`fraud-${o.id}`}><AlertOctagon size={16}/></button>
                      )}
                      <button onClick={()=>setAttaching(o)} title="Attach paid invoice" className="text-slate-300 hover:text-white p-1.5" data-testid={`attach-${o.id}`}><Paperclip size={16}/></button>
                      {o.payment_provider === "payglocal" && o.status === "paid" && (
                        <button
                          onClick={()=>refundPayGlocal(o)}
                          title="Refund via PayGlocal"
                          className="text-pink-300 hover:text-pink-200 p-1.5"
                          data-testid={`refund-payglocal-${o.id}`}
                        ><Wallet size={16}/></button>
                      )}
                      <button onClick={()=>downloadPdf(`/api/admin/orders/${o.id}/pdf`, `order-${o.id.slice(0,8).toUpperCase()}.pdf`)} title="Download PDF" className="text-slate-300 hover:text-white p-1.5" data-testid={`pdf-${o.id}`}><FileDown size={16}/></button>
                    </div>
                  </td>
                </tr>
                {expandedCascade === o.id && o.refund_cascade && (
                  <CascadeAuditDrawer order={o} colSpan={merging ? 8 : 7}/>
                )}
                {kycDrawer === o.id && (
                  <KycDrawer order={o} colSpan={merging ? 8 : 7} backend={backend}/>
                )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && <tr><td colSpan={merging ? 8 : 7} className="text-center py-10 text-slate-500">No orders match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {paying && <ManualPaymentModal order={paying} onClose={()=>setPaying(null)} onSaved={()=>{setPaying(null); load();}}/>}
      {delivering && <DeliverModal order={delivering} onClose={()=>setDelivering(null)} onSaved={()=>{setDelivering(null); load();}}/>}
      {mergeDialog && <MergeOrdersDialog orderIds={mergeDialog} onClose={()=>setMergeDialog(null)} onDone={()=>{setMergeDialog(null); clearSelection(); load();}}/>}
      {attaching && <AttachInvoiceDialog order={attaching} onClose={()=>setAttaching(null)} onDone={()=>{setAttaching(null); load();}}/>}
      {bulkOpen && <BulkContractsDialog onClose={()=>setBulkOpen(false)}/>}
    </div>
  );
}

// ============================================================================
// MERGE DIALOG
// ============================================================================
function MergeOrdersDialog({ orderIds, onClose, onDone }) {
  const [mode, setMode] = useState("combine");
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === "invoice_group") {
      api.get("/admin/orders/paid-invoices").then(r => setPaidInvoices(r.data || []))
        .catch(() => setPaidInvoices([]));
    }
  }, [mode]);

  const run = async () => {
    if (mode === "invoice_group" && !invoiceId) { toast.error("Pick a paid invoice"); return; }
    setBusy(true);
    try {
      await api.post("/admin/orders/merge", {
        order_ids: orderIds, mode,
        invoice_id: mode === "invoice_group" ? invoiceId : undefined,
      });
      toast.success(mode === "combine" ? "Merged into new order" : "Linked to invoice");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="merge-orders-dialog">
      <div className="card max-w-lg w-full p-6 space-y-4" onClick={e=>e.stopPropagation()}>
        <h3 className="text-xl font-bold text-white">Merge {orderIds.length} orders</h3>
        <div className="space-y-2">
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 cursor-pointer hover:border-blue-500">
            <input type="radio" checked={mode==="combine"} onChange={()=>setMode("combine")} data-testid="merge-mode-combine"/>
            <div>
              <div className="text-white text-sm font-semibold">Combine into a single new order</div>
              <div className="text-xs text-slate-400">Originals get marked as <code>merged</code>; a brand-new order replaces them with all line items.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 cursor-pointer hover:border-blue-500">
            <input type="radio" checked={mode==="invoice_group"} onChange={()=>setMode("invoice_group")} data-testid="merge-mode-group"/>
            <div>
              <div className="text-white text-sm font-semibold">Group under an existing paid invoice</div>
              <div className="text-xs text-slate-400">Orders stay separate but get linked to a single paid invoice (useful for retroactive batch billing).</div>
            </div>
          </label>
        </div>
        {mode === "invoice_group" && (
          <div>
            <label className="label">Paid invoice</label>
            <select value={invoiceId} onChange={e=>setInvoiceId(e.target.value)} className="input w-full" data-testid="merge-invoice-select">
              <option value="">— pick a paid invoice —</option>
              {paidInvoices.map(i => <option key={i.id} value={i.id}>{i.number} · ₹{i.amount}</option>)}
            </select>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button onClick={run} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="merge-confirm-btn">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <GitMerge size={14}/>} Confirm merge
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div></ModalPortal>
  );
}

// ============================================================================
// ATTACH INVOICE DIALOG
// ============================================================================
function AttachInvoiceDialog({ order, onClose, onDone }) {
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/paid-invoices?user_id=${order.user_id || ""}`)
      .then(r => setPaidInvoices(r.data || []))
      .catch(() => setPaidInvoices([]));
  }, [order.user_id]);

  const run = async () => {
    if (!invoiceId) { toast.error("Pick a paid invoice"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/attach-invoice`, { invoice_id: invoiceId });
      toast.success("Attached"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose} data-testid="attach-invoice-dialog">
      <div className="card max-w-lg w-full p-6 space-y-3" onClick={e=>e.stopPropagation()}>
        <h3 className="text-xl font-bold text-white">Attach paid invoice</h3>
        <p className="text-slate-400 text-xs">Link this order to an existing paid invoice for record-keeping.</p>
        <select value={invoiceId} onChange={e=>setInvoiceId(e.target.value)} className="input w-full" data-testid="attach-invoice-select">
          <option value="">— pick a paid invoice —</option>
          {paidInvoices.map(i => <option key={i.id} value={i.id}>{i.number} · ₹{i.amount}</option>)}
        </select>
        <div className="flex gap-2 pt-2">
          <button onClick={run} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="attach-confirm-btn">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Paperclip size={14}/>} Attach
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div></ModalPortal>
  );
}

function ManualPaymentModal({ order, onClose, onSaved }) {
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(order.total_inr || order.total || 0);
  const [currency, setCurrency] = useState("INR");
  const [txnDate, setTxnDate] = useState(new Date().toISOString().slice(0,10));
  const [note, setNote] = useState("");
  const [sendMail, setSendMail] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/mark-paid`, {
        method, reference, amount: Number(amount), currency,
        transaction_date: txnDate, note,
        send_confirmation_email: sendMail,
      });
      toast.success(`Marked paid · ${data.invoice_number}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="manual-payment-modal">
      <div className="card max-w-md w-full p-6">
        <h3 className="text-white font-semibold text-lg mb-3" style={{fontFamily:'Outfit'}}>Record manual payment</h3>
        <p className="text-slate-400 text-xs mb-4">Order #{order.id.slice(0,8).toUpperCase()} · Total ₹{(order.total_inr || order.total).toLocaleString()}</p>
        <div className="space-y-3">
          <div><label className="label">Method</label>
            <select className="input" value={method} onChange={e=>setMethod(e.target.value)} data-testid="mp-method">
              {["bank_transfer","cash","upi","cheque","other"].map(m => <option key={m} value={m}>{m.replace("_"," ")}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Amount</label>
              <input type="number" className="input" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="mp-amount"/>
            </div>
            <div><label className="label">Currency</label>
              <select className="input" value={currency} onChange={e=>setCurrency(e.target.value)} data-testid="mp-currency">
                {["INR","USD","EUR","GBP","AUD","CAD","SGD","AED","JPY"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="label">Transaction reference (UTR / cheque / txn id)</label>
            <input className="input" value={reference} onChange={e=>setReference(e.target.value)} placeholder="e.g. UTR201234567890" data-testid="mp-reference"/>
          </div>
          <div><label className="label">Transaction date</label>
            <input type="date" className="input" value={txnDate} onChange={e=>setTxnDate(e.target.value)} data-testid="mp-txn-date"/>
          </div>
          <div><label className="label">Note</label>
            <textarea className="input" rows="2" value={note} onChange={e=>setNote(e.target.value)} data-testid="mp-note"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={sendMail} onChange={e=>setSendMail(e.target.checked)} data-testid="mp-send-mail"/>
            Email payment confirmation + invoice PDF to customer
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="mp-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="mp-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Save & notify
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}


function DeliverModal({ order, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [sendMail, setSendMail] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/orders/${order.id}/deliver`, {
        delivery_note: note,
        send_email: sendMail,
      });
      toast.success("Service marked delivered · contract PDF emailed");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="deliver-modal">
      <div className="card max-w-md w-full p-6">
        <h3 className="text-white font-semibold text-lg mb-1" style={{fontFamily:'Outfit'}}>Mark service delivered</h3>
        <p className="text-slate-400 text-xs mb-4">Order #{order.id.slice(0,8).toUpperCase()} · {(order.items||[]).map(i=>i.name).join(", ")}</p>
        <p className="text-slate-400 text-xs mb-4">A signed Service Fulfilment Contract PDF will be auto-generated and emailed to the customer along with the delivery confirmation.</p>
        <div className="space-y-3">
          <div><label className="label">Delivery note (optional)</label>
            <textarea className="input" rows="4" value={note} onChange={e=>setNote(e.target.value)} placeholder="Summary of deliverables, hand-off instructions, login details location, etc." data-testid="dl-note"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={sendMail} onChange={e=>setSendMail(e.target.checked)} data-testid="dl-send-mail"/>
            Email customer + attach Service Fulfilment Contract PDF
          </label>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="dl-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="dl-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <PackageCheck size={14}/>} Mark delivered
          </button>
        </div>
      </div>
    </div></ModalPortal>
  );
}


// ============================================================================
// BULK CONTRACTS DIALOG — download ZIP of all Service Fulfillment Contracts
// for a date range, or batch-email each contract back to its customer.
// ============================================================================
function BulkContractsDialog({ onClose }) {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 86400000);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(iso(monthAgo));
  const [to, setTo] = useState(iso(today));
  const [statuses, setStatuses] = useState(["completed", "delivered"]);
  const [busy, setBusy] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [attachFySummary, setAttachFySummary] = useState(false);
  const [consolidate, setConsolidate] = useState(false);
  const [fyLabel, setFyLabel] = useState("");
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get("/admin/brand").then(r => setBrand(r.data)).catch(() => setBrand({}));
  }, []);

  // Indian financial year — audit packs target the PREVIOUS COMPLETED FY.
  // FY runs Apr 1 → Mar 31. After Apr 1 of year Y, the just-ended FY is (Y-1)→Y.
  // Before Apr 1 (Jan-Mar), the just-ended FY is (Y-2)→(Y-1).
  const applyYearEndPreset = () => {
    const d = new Date();
    const m = d.getMonth() + 1; // 1..12
    const y = d.getFullYear();
    const fyStart = m >= 4 ? y - 1 : y - 2;
    const fyEnd = fyStart + 1;
    const startDate = `${fyStart}-04-01`;
    const endDate = `${fyEnd}-03-31`;
    const fyLabel = `FY ${fyStart}-${String(fyEnd).slice(2)}`;
    setFrom(startDate);
    setTo(endDate);
    setStatuses(["completed", "delivered"]);
    setEmailMode(true);
    setAttachFySummary(true);
    setConsolidate(true);
    setFyLabel(fyLabel);
    setSubject(`${fyLabel} — Service Fulfillment audit pack`);
    const gst = brand?.gst_number ? `<p><strong>GST/Tax ID:</strong> <span style="font-family:monospace;">${brand.gst_number}</span></p>` : "";
    const company = brand?.brand_name || "Optivize";
    setMessage(
      `<p>Greetings,</p>` +
      `<p>Please find attached the signed Service Fulfillment Contract for your records covering <strong>${fyLabel}</strong> (1&nbsp;Apr&nbsp;${fyStart} – 31&nbsp;Mar&nbsp;${fyEnd}, Indian financial year).</p>` +
      `<p>Retain this document along with the corresponding invoice for income-tax filing, GST input-credit claims, and statutory audit purposes.</p>` +
      `${gst}` +
      `<p>For any queries or duplicate copies, reply to this email and our finance desk will respond within one business day.</p>` +
      `<p>Regards,<br/><strong>${company} — Accounts &amp; Compliance</strong></p>`
    );
    toast.success(`${fyLabel} preset applied — review subject/message before sending`);
  };

  const toggle = (s) => {
    const next = statuses.includes(s) ? statuses.filter(x => x !== s) : [...statuses, s];
    setStatuses(next.length ? next : ["completed"]);
  };

  const downloadZip = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("optivize_token");
      const r = await fetch(`${backend}/api/admin/orders/bulk-contracts.zip`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ from_date: from, to_date: to, statuses }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `service-contracts-${from}-to-${to}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Contracts ZIP downloaded");
      onClose();
    } catch (e) {
      toast.error(e.message || "Bulk export failed");
    } finally { setBusy(false); }
  };

  const emailBatch = async () => {
    if (!window.confirm(
      dryRun ? "Run a dry-run preview (no emails sent)?"
             : `Email contracts to every customer in this date range? This action cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-contracts/email", {
        from_date: from, to_date: to, statuses,
        subject: subject || undefined,
        message_html: message || undefined,
        dry_run: dryRun,
        attach_fy_summary: attachFySummary,
        consolidate_per_customer: consolidate,
        fy_label: fyLabel || undefined,
      });
      toast.success(`${dryRun ? "Dry-run: would send" : "Sent"} ${data.sent_count} · skipped ${data.skipped_count}`);
      if (!dryRun) onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email batch failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalPortal><div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
         onClick={onClose} data-testid="bulk-contracts-dialog">
      <div className="card max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Archive size={18} className="text-blue-400"/> Bulk Service Fulfillment Contracts
            </h3>
            <p className="text-slate-400 text-xs mt-1">Download a ZIP of every completed-order contract for a date range, or email each one to its customer.</p>
          </div>
          <button onClick={onClose}><X className="text-slate-400" size={18}/></button>
        </div>

        {/* Quick presets */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Quick preset:</span>
          <button onClick={applyYearEndPreset}
                  className="px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs hover:bg-amber-500/20"
                  data-testid="bulk-preset-year-end">
            🧾 Year-end pack (Indian FY)
          </button>
          {brand?.gst_number && (
            <span className="text-[10px] text-slate-500 font-mono ml-1" data-testid="bulk-gst-indicator">
              GST: {brand.gst_number}
            </span>
          )}
          {brand && !brand.gst_number && (
            <span className="text-[10px] text-amber-400/70 ml-1" data-testid="bulk-gst-missing">
              · Add GST# under Branding to include in audit pack
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="input w-full" data-testid="bulk-from"/>
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="input w-full" data-testid="bulk-to"/>
          </div>
        </div>

        <div>
          <label className="label">Include statuses</label>
          <div className="flex gap-2 flex-wrap">
            {["completed", "delivered"].map(s => (
              <label key={s} className={`cursor-pointer px-3 py-1.5 rounded-lg border text-xs ${statuses.includes(s) ? "border-blue-500 bg-blue-500/10 text-blue-200" : "border-slate-700 text-slate-400"}`}>
                <input type="checkbox" checked={statuses.includes(s)} onChange={() => toggle(s)} className="hidden" data-testid={`bulk-status-${s}`}/>
                {s}
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-800 pt-3">
          <div className="inline-flex rounded-xl bg-slate-900/60 border border-white/5 p-1 mb-3">
            <button onClick={() => setEmailMode(false)} className={`px-3 py-1.5 rounded-lg text-xs ${!emailMode ? "bg-blue-600 text-white" : "text-slate-300"}`} data-testid="bulk-mode-download">Download ZIP</button>
            <button onClick={() => setEmailMode(true)} className={`px-3 py-1.5 rounded-lg text-xs ${emailMode ? "bg-blue-600 text-white" : "text-slate-300"}`} data-testid="bulk-mode-email">Email customers</button>
          </div>

          {emailMode && (
            <div className="space-y-3" data-testid="bulk-email-fields">
              <div>
                <label className="label">Subject override (optional)</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} className="input w-full" placeholder="Leave blank to use default 'Service delivered ✅' subject" data-testid="bulk-subject"/>
              </div>
              <div>
                <label className="label">Extra message HTML (optional, appended to email body)</label>
                <textarea rows="4" value={message} onChange={e => setMessage(e.target.value)} className="input w-full font-mono text-xs" placeholder="e.g. 'For year-end tax filing, please retain the attached signed contracts.'" data-testid="bulk-message"/>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="bulk-dry-run"/>
                Dry-run only (preview recipients, don&apos;t send)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={attachFySummary} onChange={e => setAttachFySummary(e.target.checked)} data-testid="bulk-attach-fy-summary"/>
                Attach <span className="text-amber-300">FY summary PDF</span> (one-page ITR-aid: all orders, GST totals, retention notes)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={consolidate} onChange={e => setConsolidate(e.target.checked)} data-testid="bulk-consolidate"/>
                Consolidate: one email per customer (vs. one per order)
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <button onClick={onClose} className="btn-ghost !py-2 !px-4">Cancel</button>
          {emailMode ? (
            <button onClick={emailBatch} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="bulk-email-send">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Mail size={14}/>}
              {dryRun ? "Run dry-run" : "Send batch"}
            </button>
          ) : (
            <button onClick={downloadZip} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="bulk-download">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Archive size={14}/>} Download ZIP
            </button>
          )}
        </div>
      </div>
    </div></ModalPortal>
  );
}



// ============================================================================
// CASCADE AUDIT DRAWER — inline row that opens beneath a cancelled/fraud order
// to show exactly what the refund cascade did. Mirrors the data in the order's
// `refund_cascade` field so the accounts team can prove the cascade ran.
// ============================================================================
function CascadeAuditDrawer({ order, colSpan }) {
  const c = order.refund_cascade || {};
  const isFraud = c.kind === "fraud";
  const lblColor = isFraud ? "text-orange-300" : "text-rose-300";
  const ringColor = isFraud ? "border-orange-500/30 bg-orange-500/5" : "border-rose-500/30 bg-rose-500/5";
  return (
    <tr data-testid={`cascade-drawer-${order.id}`}>
      <td colSpan={colSpan} className="bg-slate-950/50 px-4 py-3">
        <div className={`rounded-lg border ${ringColor} px-4 py-3 text-xs`}>
          <div className={`flex items-center gap-2 ${lblColor} font-semibold mb-2`}>
            <ShieldAlert size={14}/>
            Refund cascade audit — {isFraud ? "FRAUD" : "CANCELLED"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-slate-300">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">When</div>
              <div className="font-mono">{c.actor_at ? fmtDate(c.actor_at) : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">By</div>
              <div className="font-mono text-amber-200">{c.actor_email || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Credits revoked</div>
              <div>
                <span className="text-rose-300 font-bold">−{c.credits_revoked_actual || 0}</span>
                {(c.credits_revoked_quoted || 0) !== (c.credits_revoked_actual || 0) && (
                  <span className="text-slate-500"> / {c.credits_revoked_quoted} quoted</span>
                )}
                <span className="text-slate-500"> AI credits</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Invoices reversed</div>
              <div className="text-rose-300 font-bold">{c.invoices_updated || 0}</div>
            </div>
          </div>
          {c.invoice_numbers && c.invoice_numbers.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Invoice numbers</div>
              <div className="flex flex-wrap gap-1.5">
                {c.invoice_numbers.map((n, i) => (
                  <span key={i} className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-800 text-rose-200 border border-rose-500/20" data-testid={`cascade-inv-${order.id}-${i}`}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}
          {c.reason && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Reason</div>
              <div className="text-slate-300 italic">{c.reason}</div>
            </div>
          )}
          {c.user_flagged && (
            <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-orange-500/10 border border-orange-500/30 text-orange-200" data-testid={`cascade-user-flagged-${order.id}`}>
              <AlertOctagon size={12}/>
              Customer account flagged for fraud review
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}


// ============================================================================
// KYC files drawer — shown when admin clicks the paperclip on an order in
// `awaiting_documents` / `documents_uploaded` state. Lists files uploaded by
// the customer and lets admin preview/download each.
// ============================================================================
function KycDrawer({ order, colSpan, backend }) {
  const [files, setFiles] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api.get(`/admin/orders/${order.id}/kyc/files`)
      .then(r => { if (alive) setFiles(r.data.files || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load KYC files"); });
    return () => { alive = false; };
  }, [order.id]);

  const open = (f) => {
    const token = localStorage.getItem("optivize_token");
    fetch(`${backend}/api/admin/orders/${order.id}/kyc/${f.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.blob() : Promise.reject(new Error("download failed")))
      .then(blob => window.open(URL.createObjectURL(blob), "_blank"))
      .catch(() => toast.error("Failed to download file"));
  };

  return (
    <tr className="bg-slate-900/40 border-b border-cyan-500/30" data-testid={`kyc-drawer-${order.id}`}>
      <td colSpan={colSpan} className="px-6 py-4">
        <div className="text-xs uppercase tracking-wider text-cyan-300 mb-2 inline-flex items-center gap-1.5">
          <Paperclip size={12}/> KYC verification documents
        </div>
        {order.documents_request_reason && (
          <div className="text-xs text-slate-400 mb-2 italic">
            Reason sent to customer: "{order.documents_request_reason}"
          </div>
        )}
        {error && <div className="text-rose-300 text-sm">{error}</div>}
        {!files && !error && <div className="text-slate-400 text-sm inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading…</div>}
        {files && files.length === 0 && (
          <div className="text-slate-500 text-sm italic">Customer has not uploaded any documents yet.</div>
        )}
        {files && files.length > 0 && (
          <ul className="divide-y divide-white/5 text-sm">
            {files.map(f => (
              <li key={f.id} className="py-2 flex items-center justify-between gap-3" data-testid={`kyc-file-${f.id}`}>
                <div className="flex-1">
                  <div className="text-white">{f.doc_type === "id_proof" ? "ID Proof" : "Address Proof"}</div>
                  <div className="text-xs text-slate-500">{f.original_filename} · {(f.size/1024).toFixed(1)} KB · {f.content_type} · {(f.uploaded_at || "").slice(0,16).replace("T"," ")}</div>
                </div>
                <button onClick={()=>open(f)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`kyc-open-${f.id}`}>
                  <FileDown size={12}/> View
                </button>
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}
